import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Modal,
    TextField,
    MenuItem,
    Grid
} from '@mui/material';
import { getData } from '../../Webservice/Webservice';

const SubcategoryForm = ({ open, handleClose, handleSubmit, isEdit, editingSubcategory }) => {
    const [formData, setFormData] = useState({
        type: isEdit ? editingSubcategory?.type || 'Subcategory' : 'Subcategory',
        linkId: isEdit ? editingSubcategory?.linkId?._id || '' : '',
        status: isEdit ? editingSubcategory?.status || 'active' : 'active',
        showTo: isEdit ? editingSubcategory?.showTo || 'all' : 'all',
        position: isEdit ? editingSubcategory?.position || '' : '',
        image: isEdit ? editingSubcategory?.image || null : null,
    });
    const [subcategories, setSubcategories] = useState([]);
    const [imagePreview, setImagePreview] = useState(null);

    // Fetch available subcategories for linkId selection
    useEffect(() => {
        const fetchSubcategories = async () => {
            try {
                const tokenValue = sessionStorage.getItem('token');
                const token = tokenValue ? `Bearer ${tokenValue}` : '';
                const response = await getData('subcategories/list?limit=1000', null, token);
                if (response && Array.isArray(response.subcategories)) {
                    setSubcategories(response.subcategories);
                } else {
                    setSubcategories([]);
                }
            } catch (error) {
                console.error('Error fetching subcategories for selection:', error);
                setSubcategories([]);
            }
        };
        fetchSubcategories();
    }, []);

    // Set initial image preview for edit mode
    useEffect(() => {
        if (isEdit && editingSubcategory?.image) {
            setImagePreview(editingSubcategory.image);
        } else {
            setImagePreview(null);
        }

        if (isEdit && editingSubcategory) {
            setFormData({
                type: editingSubcategory.type || 'Subcategory',
                linkId: editingSubcategory.linkId?._id || editingSubcategory.linkId || '',
                status: editingSubcategory.status || 'active',
                showTo: editingSubcategory.showTo || 'all',
                position: editingSubcategory.position || '',
                image: editingSubcategory.image || null,
            });
        } else {
            setFormData({
                type: 'Subcategory',
                linkId: '',
                status: 'active',
                showTo: 'all',
                position: '',
                image: null,
            });
        }
    }, [isEdit, editingSubcategory, open]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData((prev) => ({ ...prev, image: file }));
        if (file) {
            setImagePreview(URL.createObjectURL(file));
        } else {
            setImagePreview(null);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        handleSubmit(formData);
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={{ p: 4, backgroundColor: '#fff', borderRadius: '8px', maxWidth: '500px', margin: 'auto', mt: '10%' }}>
                <Typography variant="h6" sx={{ fontFamily: 'Mulish, sans-serif', mb: 3 }}>
                    {isEdit ? 'Edit Home Category' : 'Add Home Category'}
                </Typography>
                <form onSubmit={onSubmit}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                select
                                label="Type"
                                name="type"
                                value={formData.type}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            >
                                <MenuItem value="Subcategory">Subcategory</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                select
                                label="Subcategory"
                                name="linkId"
                                value={formData.linkId}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            >
                                {subcategories.map((subcat) => (
                                    <MenuItem key={subcat._id} value={subcat._id}>
                                        {subcat.title}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Position"
                                name="position"
                                type="number"
                                value={formData.position}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                select
                                label="Status"
                                name="status"
                                value={formData.status}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                select
                                label="Show To"
                                name="showTo"
                                value={formData.showTo}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="b2b">B2B</MenuItem>
                                <MenuItem value="b2c">B2C</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>

                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ marginBottom: '16px' }}
                    />
                    {imagePreview && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'Mulish, sans-serif', mb: 1 }}>
                                Image Preview:
                            </Typography>
                            <img
                                src={imagePreview}
                                alt="Preview"
                                style={{ maxWidth: '90%', maxHeight: '100px', objectFit: 'contain' }}
                            />
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            sx={{
                                fontFamily: 'Mulish, sans-serif',
                                color: 'red',
                                border: '1px solid red',
                                '&:hover': { borderColor: 'red', backgroundColor: 'rgba(255,0,0,0.1)' }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{
                                fontFamily: 'Mulish, sans-serif',
                                background: 'rgb(255 209 2)',
                                color: 'black',
                                border: '1px solid black',
                                '&:hover': { background: 'rgb(255 209 2)' }
                            }}
                        >
                            {isEdit ? 'Update' : 'Add'}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default SubcategoryForm;